import React, { useEffect, useState } from "react";
import styles from "./PromotionDashboard.module.scss";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  DatePicker, 
  Space,
  Spin,
  Button
} from "antd";
import { 
  RiseOutlined,
  DollarOutlined,
  ShoppingOutlined
} from "@ant-design/icons";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import moment, { Moment } from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Legend
);

const { RangePicker } = DatePicker;

const PromotionDashboard = () => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
  const [pendingRange, setPendingRange] = useState<[Moment, Moment] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [promotionAlerts] = useState([
    {
      id: 1,
      type: "expiring",
      title: "Promoción próxima a finalizar",
      severity: "warning"
    },
    {
      id: 2,
      type: "conversion",
      title: "Promoción con baja conversión",
      severity: "warning"
    },
    {
      id: 3,
      type: "demand",
      title: "Artículos en promoción con baja demanda",
      severity: "warning"
    },
    {
      id: 4,
      type: "conflict",
      title: "Conflicto de promociones",
      severity: "error"
    },
    {
      id: 5,
      type: "application",
      title: "Promoción no aplicada correctamente en una sucursal",
      severity: "error"
    }
  ]);

  const promotionStatusData = {
    labels: ["Borrador", "Enviado", "Completada"],
    datasets: [
      {
        data: [34900, 432000, 512000],
        backgroundColor: ["#f5222d", "#8b5cf6", "#fa8c16"],
        borderWidth: 0,
      },
    ],
  };

  const portfolioImpactData = {
    labels: ["Tienda 1", "Tienda 2", "Tienda 3", "Tienda 4"],
    datasets: [
      {
        data: [34900, 432000, 512000, 672000],
        backgroundColor: ["#f5222d", "#8b5cf6", "#1890ff", "#fa8c16"],
        borderWidth: 0,
      },
    ],
  };

  const topPromotionsData = {
    labels: ["Producto 1", "Producto 2", "Producto 3", "Producto 4", "Producto 5"],
    datasets: [
      {
        label: "ROI",
        data: [580, 480, 380, 280, 180],
        backgroundColor: "#1890ff",
        borderRadius: 4,
      },
    ],
  };

  const categorySalesData = {
    labels: ["Categoría 1", "Categoría 2", "Categoría 3", "Categoría 4", "Categoría 5"],
    datasets: [
      {
        label: "Distribución de ventas (%)",
        data: [35, 25, 20, 15, 5],
        backgroundColor: ["#8b5cf6", "#1890ff", "#f5222d", "#fa8c16", "#52c41a"],
        borderRadius: 4,
      },
    ],
  };

  const kpis = {
    grossMargin: 890,
    netMargin: 1234,
    averageTicket: 890,
    unitsPerTransaction: 890
  };

  useEffect(() => {
    const today = moment();
    const lastWeek = today.clone().subtract(1, "week");
    setDateRange([lastWeek, today]);
    setPendingRange([lastWeek, today]);
  }, []);

  const handleUpdate = () => {
    if (pendingRange) {
      setDateRange(pendingRange);
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
    },
  };

  return (
    <div className={styles.dashboardWrapper}>
      {/* Header con filtros */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerContent}>
          <Space size="middle">
            <select className={styles.branchSelector}>
              <option>Tienda</option>
              <option>Tienda 1</option>
              <option>Tienda 2</option>
              <option>Tienda 3</option>
              <option>Tienda 4</option>
            </select>
            <RangePicker
              value={pendingRange}
              onChange={(dates) => setPendingRange(dates as [Moment, Moment])}
              allowClear={false}
              placeholder={["Inicio", "Fin"]}
            />
            <Button type="primary" onClick={handleUpdate} className={styles.updateButton}>
              Actualizar
            </Button>
          </Space>
        </div>
      </div>

      {/* KPIs */}
      <div className={styles.kpisSection}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Card className={styles.kpiCard}>
              <Spin spinning={isLoading}>
                <Statistic
                  title="Margen Bruto"
                  value={formatNumber(kpis.grossMargin)}
                  prefix={<RiseOutlined className={styles.kpiIcon} />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div className={styles.kpiChange}>+18%</div>
                <div className={styles.kpiSubtext}>+1000 esta semana</div>
              </Spin>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className={styles.kpiCard}>
              <Spin spinning={isLoading}>
                <Statistic
                  title="Margen Neto"
                  value={formatNumber(kpis.netMargin)}
                  prefix={<RiseOutlined className={styles.kpiIcon} />}
                  valueStyle={{ color: '#1890ff' }}
                />
                <div className={styles.kpiChange}>+18%</div>
                <div className={styles.kpiSubtext}>+2.8k this week</div>
              </Spin>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className={styles.kpiCard}>
              <Spin spinning={isLoading}>
                <Statistic
                  title="Ticket promedio"
                  value={formatNumber(kpis.averageTicket)}
                  prefix={<DollarOutlined className={styles.kpiIcon} />}
                  valueStyle={{ color: '#722ed1' }}
                />
                <div className={styles.kpiChange}>+18%</div>
                <div className={styles.kpiSubtext}>+2.8k this week</div>
              </Spin>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className={styles.kpiCard}>
              <Spin spinning={isLoading}>
                <Statistic
                  title="Unidades por transacción"
                  value={formatNumber(kpis.unitsPerTransaction)}
                  prefix={<ShoppingOutlined className={styles.kpiIcon} />}
                  valueStyle={{ color: '#f5222d' }}
                />
                <div className={styles.kpiChangeNegative}>+18%</div>
                <div className={styles.kpiSubtext}>+1.2k this week</div>
              </Spin>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Gráficos principales */}
      <Row gutter={[16, 16]} className={styles.chartsSection}>
        <Col xs={24} lg={8}>
          <Card 
            title="Impacto en portafolio"
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              <Doughnut data={portfolioImpactData} options={doughnutOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title="Top promociones"
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              <Bar data={topPromotionsData} options={chartOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title="Distribución de ventas por categoría"
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              <Bar data={categorySalesData} options={chartOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Segunda fila de gráficos */}
      <Row gutter={[16, 16]} className={styles.chartsSection}>
        <Col xs={24} lg={12}>
          <Card 
            title="Promociones por estado"
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              <Doughnut data={promotionStatusData} options={doughnutOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Alertas de Promociones"
            className={styles.alertsCard}
          >
            <div className={styles.alertsList}>
              {promotionAlerts.map((alert) => (
                <div key={alert.id} className={styles.alertItem}>
                  <span className={styles.alertArrow}>→</span>
                  <span className={styles.alertText}>{alert.title}</span>
                </div>
              ))}
              <Button type="link" className={styles.seeAllButton}>
                Ver todo
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PromotionDashboard;
